<script setup lang="ts">
import { useDatalayerSetup } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useDatalayerSetup'
import { useParamPage } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useParamPage'

const app = useNuxtApp()

const { isIos } = useDevice()

const { deferredBrandTheme } = useTheme()

const { getLocale } = useI18n()
const locale = getLocale()
const route = useRoute()
const { initCart, initCompare } = useCart()
const { checkoutUrl, cartUrl } = useRouteHelper()
const { mergeWishlists, refreshWishlistOnTabChange, initWishlist } =
  useWishlist()
const { addWishlistTabChangeListener, removeWishlistTabChangeListener } =
  refreshWishlistOnTabChange()

const isCheckoutPage = computed(() => route.name === 'localized-checkout')
const isCartPage = computed(() => route.path.includes(cartUrl))
const { checkScript } = useXgen()

let isDatalayerSetupInitialized = false

const initializeDLSetupParamPageGenericPages = () => {
  // Page is a generic page only if is not amplience or product-based
  if (!(app.payload['is_product'] || !!app.payload['amplience_page_type'])) {
    useParamPage('genericPage')
  }
}

onMounted(async () => {
  // Remove the legacy Partytown service worker. Fire-and-forget,
  // run first so an earlier await rejecting can't skip it.
  usePartytownCleanup()
  // Both are long serial chains of network calls, they touch different state and
  // neither reads the other's result, so they were queueing for no reason.
  // mergeWishlists below still runs after initWishlist: it diffs against the
  // wishlist state that initWishlist populates, so it stays where it is.
  await Promise.all([initCart(), initWishlist()])
  // randomUUID if present, random 10 digits number if possible
  useRiskified(self?.crypto?.randomUUID?.() ?? Math.floor(Math.random() * 1e10))
  useDatacloud()
  checkScript()
  addWishlistTabChangeListener()
  await mergeWishlists()
  if (isCheckoutPage.value || isCartPage.value) {
    initCompare()
  }
  initializeDLSetupParamPageGenericPages()
  useDatalayerSetup()
  const { customer } = await useCustomer()
  // Imported here rather than at the top of the file: a static import pulls the
  // whole RUM package into the entry chunk, which is the first script the page
  // loads and the one nothing else can start without. The datadog plugin already
  // imports it dynamically, so by the time this runs the module is in cache and
  // this resolves immediately.
  //
  // Guarded like the plugin, checking the value and not only the promise. A
  // rejection here reaches Vue's error handler, and during mount that is still
  // Nuxt's - which would blank the page exactly as the plugin did. It survives
  // today only because the awaits above it outlast the handler being cleared,
  // which is not a safety anyone should have to preserve by hand.
  const rum = await import('@datadog/browser-rum').catch(() => undefined)
  if (customer?.value?.id) {
    rum?.datadogRum?.setUser({
      id: customer.value.id.replace('auth0|', ''),
      name: customer.value.firstName + ' ' + customer.value.lastName,
      email: customer.value.email,
    })
  } else {
    rum?.datadogRum?.setUser({})
  }
})

onBeforeUnmount(() => {
  removeWishlistTabChangeListener()
})

useHead({
  htmlAttrs: {
    lang: locale === 'en-wx' ? 'en' : locale,
  },
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1',
    },
  ],
})

useHead({
  titleTemplate: (title?: string) =>
    title && title !== 'undefined' ? title : 'Armani',
})

// Trigger send event on route change
useScriptEventPage(async () => {
  initializeDLSetupParamPageGenericPages()
  if (isDatalayerSetupInitialized) {
    useDatalayerSetup()
  }
  isDatalayerSetupInitialized = true
})
// Use SSR-safe IDs for Headless UI
// https://github.com/P4sca1/nuxt-headlessui#quick-setup
provideHeadlessUseId(() => useId())
const isDatadogTest = useState<boolean>('isDatadogTest')
</script>

<template>
  <div>
    <UtilsSkipLink />
    <NuxtLoadingIndicator
      class="bg-background-primary"
      :class="`theme-${deferredBrandTheme}`"
      :color="false"
      :height="2"
    />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <AtomsLoaderArmani />
    <ClientOnly v-if="!isDatadogTest">
      <CookieBanner />
    </ClientOnly>
  </div>
</template>

<style lang="scss">
#onetrust-consent-sdk {
  display: none;
}
</style>

<style lang="scss">
@import '@design-system/assets/css/tailwind.scss';
</style>
